// eslint-disable-next-line import/no-extraneous-dependencies
import { dayjsTZ } from "helpers";
import { utils, CellObject, writeFile, Range } from "xlsx-js-style";

type CellObjectExcel = CellObject | string;

export enum DataType {
  Percent = "percent",
  Number = "number",
  String = "string",
  Date = "date",
}

const BORDER_ALL = {
  border: {
    top: { style: "thin", color: { rgb: "000000" } },
    right: { style: "thin", color: { rgb: "000000" } },
    bottom: { style: "thin", color: { rgb: "000000" } },
    left: { style: "thin", color: { rgb: "000000" } },
  },
};

interface DataItemStyle {
  color?: string;
  fontWeight?: string;
  background?: string;
  fontFamily?: string;
  fontSize?: string;
  textAlign?: string;
}

export interface DataItem {
  value: string;
  dataType: string;
  style?: DataItemStyle;
}

export type DataExcel = DataItem[][];

export const formatColorValue = (text: string): string =>
  text?.replace("#", "");

const convertStringToNumber = (val: string): number | string => {
  if (
    (val?.match(/\./g) || []).length <= 1 &&
    (val?.match(/,/g) || []).length === 0
  ) {
    return parseFloat(val);
  }

  const num = parseFloat(val.replace(/,/g, "")); // 13,743.50 => 13743.50

  return Number.isNaN(num) ? val : num;
};

export const getCellWithStyle = ({
  value,
  bold,
  italic,
  underline,
  sz,
  color,
  bgColor,
  textAlign,
  fontName,
  dataType,
  noBorder = true,
  border = [],
  formula,
  wrapText = true,
}: {
  value: string | number;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  noBorder?: boolean;
  sz?: number;
  bgColor?: string;
  color?: string;
  textAlign?: string;
  fontName?: string;
  dataType?: DataType;
  border?: ("top" | "right" | "bottom" | "left")[];
  formula?: string;
  wrapText?: boolean;
}): CellObject => {
  let t = "s"; // Type string
  let z = "@";
  let v: string | number = value;
  let alignmentHorizontal = textAlign || "left";
  let f = "";

  if (dataType === DataType.Percent && value) {
    t = "n"; // Type number
    z = "0.00%";
    v = Number(convertStringToNumber(String(value || ""))) / 100;
  }

  if (dataType === DataType.Number) {
    alignmentHorizontal = "right";
    t = "n";

    const validValue = !!value; // Exclusve `""`, `null`, `undefined`
    const numValue = Number(value);
    v = !validValue || Number.isNaN(numValue) ? 0 : numValue;
    const separator = value.toString().split(".")[1] ?? "";
    z =
      separator.length > 0
        ? `#,##0.${Array(separator.length).fill("0").join("")}`
        : "#,##0";
  }

  if (dataType === DataType.Date && value) {
    t = "d";
    z = "dd/mm/yyyy";
    v = dayjsTZ(value).toString();
  }

  if (t === "s") {
    v = v || ""; // Avoid null or undefined
  }

  if (formula) {
    if (dataType === DataType.Number) z = "#,##0.0";
    f = formula;
  }

  return {
    v,
    t,
    z,
    s: {
      font: {
        ...(bold && { bold: true }),
        ...(italic && { italic: true }),
        ...(underline && { underline: true }),
        color: { rgb: color || "000000" },
        sz: sz || 10,
        name: fontName || "Arial",
      },
      alignment: {
        horizontal: alignmentHorizontal,
        vertical: "center",
        wrapText,
      },
      ...(bgColor && { fill: { fgColor: { rgb: bgColor }, bgColor } }),
      ...(!noBorder && BORDER_ALL),
      ...(border?.includes("top") && {
        border: { top: { style: "thin", color: { rgb: "000000" } } },
      }),
      ...(border?.includes("bottom") && {
        border: { bottom: { style: "thin", color: { rgb: "000000" } } },
      }),
      ...(border?.includes("left") && {
        border: { left: { style: "thin", color: { rgb: "000000" } } },
      }),
      ...(border?.includes("right") && {
        border: { right: { style: "thin", color: { rgb: "000000" } } },
      }),
    },
    ...(f && { f }),
  } as CellObject;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mappingStyle = (col: any, value: string) => ({
  value: value || "",
  bold: col?.style?.fontWeight === "bold",
  color: formatColorValue(String(col?.style?.color || "")),
  bgColor: formatColorValue(String(col?.style?.background || "")),
  textAlign: String(col?.style?.textAlign || ""),
  dataType:
    // eslint-disable-next-line no-nested-ternary
    col?.dataType === "number" && col?.type !== "head"
      ? String(value || "")?.includes("%")
        ? DataType.Percent
        : DataType.Number
      : DataType.String,
});

export const renderTableExcel = ({ data }: { data: DataExcel }) => {
  const result: CellObjectExcel[][] = [] as CellObjectExcel[][];

  data.forEach((row) => {
    const rowExcel = [] as CellObjectExcel[];
    row.forEach((col) => {
      rowExcel.push(getCellWithStyle(mappingStyle(col, col.value)));
    });
    result.push(rowExcel);
  });
  const ws = utils.aoa_to_sheet([result?.[0]]);
  result?.shift();

  // Render to sheet file
  utils.sheet_add_aoa(ws, [...(result || [])], {
    origin: -1,
  });

  return ws;
};

export const renderTableExcelJSON = ({ data }: { data: DataExcel }) => {
  const result: CellObjectExcel[][] = [] as CellObjectExcel[][];

  data.forEach((row) => {
    const rowExcel = [] as CellObjectExcel[];
    row.forEach((col) => {
      rowExcel.push(getCellWithStyle(mappingStyle(col, col.value)));
    });
    result.push(rowExcel);
  });

  return result;
};

export function writeFileExcel({
  data,
  fileName,
}: {
  data: DataExcel;
  fileName: string;
}) {
  const workbook = utils.book_new();

  const ws = renderTableExcel({ data });
  utils.book_append_sheet(workbook, ws, fileName);

  writeFile(workbook, `${fileName}.xlsx`, {
    bookType: "xlsx",
    type: "buffer",
  });
}

export function writeFileTemplate({
  fileName,
  data,
  merges,
  widths,
  heights,
}: {
  fileName: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[][];
  merges: Range[];
  widths: { width: number }[];
  heights?: { hpx: number }[];
}) {
  const workbook = utils.book_new();
  const ws = utils.aoa_to_sheet(data);
  utils.book_append_sheet(workbook, ws, fileName);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  ws["!merges"] = [...(ws["!merges"] || []), ...merges];
  if (widths) {
    ws["!cols"] = widths;
  }
  if (heights) {
    ws["!rows"] = heights;
  }

  writeFile(workbook, `${fileName}.xlsx`, {
    bookType: "xlsx",
    type: "buffer",
  });
}

export function writeFileMultiTemplate({
  fileName,
  dataByTab,
}: {
  fileName: string;
  dataByTab: {
    name: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any[][];
    merges?: Range[];
    widths?: { width: number }[];
    heights?: { hpx: number }[];
  }[];
}) {
  const workbook = utils.book_new();

  // for each dataByTab
  dataByTab.forEach((tab) => {
    const ws = utils.aoa_to_sheet(tab.data);
    utils.book_append_sheet(workbook, ws, tab.name);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    ws["!merges"] = [...(ws["!merges"] || []), ...(tab.merges || [])];
    if (tab.widths) {
      ws["!cols"] = tab.widths;
    }
    if (tab.heights) {
      ws["!rows"] = tab.heights;
    }
  });

  writeFile(workbook, `${fileName}.xlsx`, {
    bookType: "xlsx",
    type: "buffer",
  });
}

export function getWidthsXlsx(widths: number[]): { width: number }[] {
  return widths.map((width) => ({ width }));
}

export function getHeightsXlsx(heights: number[]): { hpx: number }[] {
  return heights.map((hpx) => ({ hpx }));
}

export function getMergesXlsx(params: string[][]): Range[] {
  return params.map(([start, end, init]) => {
    const [s1, s2] = start.split(":");
    const [e1, e2] = end.split(":");
    const [initRow = 0, initCol = 0] = init?.split(":") || [];
    return {
      s: {
        r: Number(s1) - 1 + Number(initRow),
        c: Number(s2) - 1 + Number(initCol),
      },
      e: {
        r: Number(e1) - 1 + Number(initRow),
        c: Number(e2) - 1 + Number(initCol),
      },
    };
  });
}

export const getJSONTemplateMasterData = (
  title: string,
  data: {
    code: string;
    name?: string;
    nativeName: string;
  }[]
) => {
  const cell = (
    value: string,
    config?: Partial<Parameters<typeof getCellWithStyle>[0]>
  ) => {
    return getCellWithStyle({
      sz: 10,
      ...config,
      value,
    });
  };

  return [
    [],
    [cell(title, { noBorder: true, bold: true, textAlign: "left" })],
    [
      cell("Mã / Code", {
        bold: true,
        textAlign: "left",
        bgColor: "D9D9D9",
        noBorder: false,
      }),
      cell("Tên / Name", {
        bold: true,
        textAlign: "left",
        bgColor: "D9D9D9",
        noBorder: false,
      }),
    ],
    ...(data?.map((e) => [
      cell(e.code, { noBorder: false, color: "B74C27" }),
      cell(`${e.nativeName}${e.name ? ` / ${e.name}` : ""}`, {
        noBorder: false,
      }),
    ]) as never),
  ];
};
